import { useState, useEffect } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity, DollarSign, TrendingUp, TrendingDown, BarChart3, Target,
  Clock, Cpu, MemoryStick, Zap, AlertTriangle, ArrowUpRight, ArrowDownRight,
  HeartPulse, Wifi, WifiOff,
} from "lucide-react";
import api from "@/lib/api";
import PnlSummary from "./PnlSummary";
import InfoTip from "./InfoTip";
import { PositionCards } from "./PhoneCards";

const stripCls = "flex items-center gap-2 cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--ops-brand))]";

function StatCard({ testId, label, value, icon: Icon, sub, color, mono = true, onClick, ariaLabel }) {
  const clickable = onClick ? {
    role: "link", tabIndex: 0, "aria-label": ariaLabel, onClick,
    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } },
  } : {};
  return (
    <Card data-testid={testId} {...clickable} className={`p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-150 ${onClick ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--ops-brand))]' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))] mb-1">{label}</div>
          <div className={`text-lg max-sm:text-base font-semibold ${mono ? 'font-mono-num' : ''} ${color || 'text-[hsl(var(--ops-text))]'}`}>{value}</div>
          {sub && <div className="text-[11px] text-[hsl(var(--ops-text-muted))] mt-0.5">{sub}</div>}
        </div>
        {Icon && <Icon className={`size-4 shrink-0 ${color || 'text-[hsl(var(--ops-text-muted))]'}`} />}
      </div>
    </Card>
  );
}

export default function Overview() {
  const { autoRefresh } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await api.get("/ops/overview");
        setData(d);
        setTick(t => t + 1); // Force age recalc only when fresh data arrives
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
    if (!autoRefresh) return;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [autoRefresh]);

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48 bg-[hsl(var(--ops-surface))]" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{Array.from({length:8}).map((_,i)=><Skeleton key={i} className="h-24 bg-[hsl(var(--ops-surface))]" />)}</div>
    </div>
  );

  const s = data?.status || {};
  const a = data?.account || {};
  const t = data?.today || {};
  const brain = data?.brain_summary || {};
  const alerts = data?.alerts || {};
  const pnlColor = (v) => v > 0 ? 'ops-long' : v < 0 ? 'ops-short' : 'text-[hsl(var(--ops-text))]';
  const fmtPnl = (v) => {
    if (v == null) return '$0.00';
    const sign = v > 0 ? '+' : v < 0 ? '-' : '';
    return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const fmtMoney = (v) => v != null ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';

  // Heartbeat age calculation
  const lastHb = s.last_heartbeat;
  const hbAge = lastHb ? Math.round((Date.now() - new Date(lastHb).getTime()) / 1000) : null;
  const hbFresh = hbAge !== null && hbAge < 30;
  const hbStale = hbAge !== null && hbAge >= 30;
  const hbNever = hbAge === null;

  const fmtHbAge = (sec) => {
    if (sec === null) return 'Never';
    if (sec < 5) return 'Just now';
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s ago`;
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ago`;
  };

  return (
    <div data-testid="ops-overview-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">Operations Overview</h1>
          <p className="text-xs text-[hsl(var(--ops-text-muted))]">Real-time MentorAI system status</p>
        </div>
        {alerts.critical > 0 && (
          <Link to="/ops/logs" data-testid="ops-critical-alert-link">
            <Badge className="rounded-md bg-[hsl(var(--ops-critical))]/15 text-[hsl(var(--ops-critical))] border-0 text-[11px] gap-1 cursor-pointer hover:bg-[hsl(var(--ops-critical))]/25 transition-colors duration-150">
              <AlertTriangle className="size-3" /> {alerts.critical} Critical
            </Badge>
          </Link>
        )}
      </div>

      {/* MentorAI Connection Status */}
      <Card data-testid="ops-heartbeat-status" className={`p-3 border overflow-hidden ${
        hbFresh
          ? 'bg-[hsl(var(--ops-long))]/5 border-[hsl(var(--ops-long))]/20'
          : hbStale
            ? 'bg-[hsl(var(--ops-critical))]/5 border-[hsl(var(--ops-critical))]/20'
            : 'bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]'
      }`}>
        <div className="flex items-center justify-between max-sm:gap-2">
          <div className="flex items-center gap-3 max-sm:min-w-0">
            <div className={`size-8 rounded-lg max-sm:shrink-0 flex items-center justify-center ${
              hbFresh ? 'bg-[hsl(var(--ops-long))]/15' : hbStale ? 'bg-[hsl(var(--ops-critical))]/15' : 'bg-[hsl(var(--ops-text-muted))]/10'
            }`}>
              <HeartPulse className={`size-4 ${
                hbFresh ? 'text-[hsl(var(--ops-long))] animate-pulse' : hbStale ? 'text-[hsl(var(--ops-critical))]' : 'text-[hsl(var(--ops-text-muted))]'
              }`} />
            </div>
            <div className="max-sm:min-w-0">
              <div className="flex items-center gap-2 max-sm:flex-wrap max-sm:gap-y-1">
                <span className="text-sm font-semibold text-[hsl(var(--ops-text))]">MentorAI Connection</span>
                <Badge className={`rounded text-[10px] border-0 px-1.5 ${
                  hbFresh
                    ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]'
                    : hbStale
                      ? 'bg-[hsl(var(--ops-critical))]/15 text-[hsl(var(--ops-critical))]'
                      : 'bg-[hsl(var(--ops-text-muted))]/15 text-[hsl(var(--ops-text-muted))]'
                }`}>
                  {hbFresh ? <><Wifi className="size-3 mr-1" /> CONNECTED</> : hbStale ? <><WifiOff className="size-3 mr-1" /> STALE</> : 'NO HEARTBEAT'}
                </Badge>
              </div>
              <div className="text-[11px] text-[hsl(var(--ops-text-muted))] mt-0.5">
                {hbNever
                  ? 'Waiting for first heartbeat from MentorAI...'
                  : <>Last heartbeat: <span className="font-mono-num">{fmtHbAge(hbAge)}</span> {lastHb && <span className="ml-1 opacity-60">({new Date(lastHb).toLocaleTimeString()})</span>}</>
                }
              </div>
            </div>
          </div>
          {hbFresh && (
            <div className="flex items-center gap-1.5 max-sm:shrink-0">
              <span className="relative flex size-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--ops-long))] opacity-75"></span>
                <span className="relative inline-flex rounded-full size-2 bg-[hsl(var(--ops-long))]"></span>
              </span>
              <span className="text-[10px] text-[hsl(var(--ops-long))] font-medium">LIVE</span>
            </div>
          )}
        </div>
      </Card>

      {/* System info strip: every item is labelled, with a plain-English tip on hover or tap */}
      <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          <InfoTip tip="Version of the MentorAI trading program running on Eli's trading laptop.">
            <button type="button" data-testid="ops-strip-engine" className={stripCls}>
              <span className="text-[hsl(var(--ops-text-muted))]">Engine</span>
              <span className="font-mono-num text-[hsl(var(--ops-text))]">{s.engine_version || '—'}</span>
            </button>
          </InfoTip>
          <InfoTip tip="The futures contract the bot is trading right now.">
            <button type="button" data-testid="ops-strip-contract" className={stripCls}>
              <span className="text-[hsl(var(--ops-text-muted))]">Contract</span>
              <Badge className="rounded text-[10px] bg-[hsl(var(--ops-brand))]/15 text-[hsl(var(--ops-brand))] border-0 px-1.5">{s.current_contract || '—'}</Badge>
            </button>
          </InfoTip>
          <InfoTip tip="Whether the futures market is open for trading, as reported by the bot.">
            <button type="button" data-testid="ops-strip-market" className={stripCls}>
              <span className="text-[hsl(var(--ops-text-muted))]">Market</span>
              <Badge className={`rounded text-[10px] border-0 px-1.5 ${s.market_status === 'open' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-text-muted))]/15 text-[hsl(var(--ops-text-muted))]'}`}>
                {(s.market_status || 'unknown').toUpperCase()}
              </Badge>
            </button>
          </InfoTip>
          {s.cpu_usage_pct != null && (
            <InfoTip tip={`Laptop CPU: how busy Eli's trading laptop is. Sent by the bot with every heartbeat${hbFresh ? '' : ' (this is the last value it sent)'}.`}>
              <button type="button" data-testid="ops-strip-cpu" className={stripCls}>
                <Cpu className="size-3 text-[hsl(var(--ops-text-muted))]" />
                <span className="hidden sm:inline text-[hsl(var(--ops-text-muted))]">CPU</span>
                <span className="font-mono-num text-[hsl(var(--ops-text))]">{Math.round(s.cpu_usage_pct)}%</span>
              </button>
            </InfoTip>
          )}
          {s.memory_usage_pct != null && (
            <InfoTip tip={`Laptop memory: how much of the trading laptop's working memory is in use (not disk space). Sent by the bot with every heartbeat${hbFresh ? '' : ' (this is the last value it sent)'}.`}>
              <button type="button" data-testid="ops-strip-memory" className={stripCls}>
                <MemoryStick className="size-3 text-[hsl(var(--ops-text-muted))]" />
                <span className="hidden sm:inline text-[hsl(var(--ops-text-muted))]">Memory</span>
                <span className="font-mono-num text-[hsl(var(--ops-text))]">{Math.round(s.memory_usage_pct)}%</span>
              </button>
            </InfoTip>
          )}
          {s.uptime_seconds > 0 && (
            <InfoTip tip="Bot uptime: how long the bot has been running since it last started.">
              <button type="button" data-testid="ops-strip-uptime" className={stripCls}>
                <Clock className="size-3 text-[hsl(var(--ops-text-muted))]" />
                <span className="hidden sm:inline text-[hsl(var(--ops-text-muted))]">Uptime</span>
                <span className="font-mono-num text-[hsl(var(--ops-text))]">{`${Math.floor(s.uptime_seconds / 3600)}h ${Math.floor((s.uptime_seconds % 3600) / 60)}m`}</span>
              </button>
            </InfoTip>
          )}
          <InfoTip tip="Heartbeat: the bot checks in with this dashboard every few seconds. This is how long ago the last check-in arrived. Over 30 seconds means the connection may be down.">
            <button type="button" data-testid="ops-strip-heartbeat" className={stripCls}>
              <HeartPulse className={`size-3 ${hbFresh ? 'text-[hsl(var(--ops-long))]' : 'text-[hsl(var(--ops-text-muted))]'}`} />
              <span className="text-[hsl(var(--ops-text-muted))]">Heartbeat</span>
              <span className={`font-mono-num ${hbFresh ? 'text-[hsl(var(--ops-long))]' : hbStale ? 'text-[hsl(var(--ops-critical))]' : 'text-[hsl(var(--ops-text))]'}`}>{fmtHbAge(hbAge)}</span>
            </button>
          </InfoTip>
        </div>
      </Card>

      {/* Realized P&L (paper account only): Today / Week / Month / Last 12 mo / YTD + 30-day daily bars */}
      <PnlSummary autoRefresh={autoRefresh} accountType="SIM" title="P&L · Paper (Sim101)" />

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testId="ops-account-balance" label="Account Balance" value={fmtMoney(a.balance)} icon={DollarSign} sub={`Net Liq: ${fmtMoney(a.net_liquidation)}`} />
        <StatCard testId="ops-today-pnl-value" label="Today's P&L" value={fmtPnl(t.pnl)} icon={t.pnl >= 0 ? TrendingUp : TrendingDown} color={pnlColor(t.pnl)} sub={`${t.trades || 0} trades`} />
        <StatCard testId="ops-today-winrate" label="Win Rate (Today)" value={`${t.win_rate ?? 0}%`} icon={Target} sub={`${t.wins || 0}W / ${t.losses || 0}L`} />
        <StatCard testId="ops-mentor-score-overview" label="Mentor Score" value={brain.mentor_score ?? '—'} icon={Zap} color="ops-brand" sub={`${brain.direction || '—'} • Gate: ${brain.trade_gate || '—'}`}
          onClick={() => navigate("/ops/brain")} ariaLabel="Open MentorAI Brain" />
        <StatCard testId="ops-unrealized-pnl" label="Unrealized P&L" value={fmtPnl(a.pnl_unrealized)} icon={Activity} color={pnlColor(a.pnl_unrealized)} />
        <StatCard testId="ops-realized-pnl" label="Realized P&L" value={fmtPnl(a.pnl_realized)} icon={BarChart3} color={pnlColor(a.pnl_realized)} />
        <StatCard testId="ops-margin-used" label="Margin Used" value={fmtMoney(a.margin_used)} icon={DollarSign} sub={`Buying Power: ${fmtMoney(a.buying_power)}`} />
        <StatCard testId="ops-open-positions-count" label="Open Positions" value={a.open_positions ?? 0} icon={Activity} mono={false} />
      </div>

      {/* Open Positions Preview */}
      {data?.positions?.length > 0 && (
        <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
          <div className="px-3 py-2 border-b border-[hsl(var(--ops-border))] flex items-center justify-between">
            <span className="text-sm font-semibold text-[hsl(var(--ops-text))]">Open Positions</span>
            <Link to="/ops/trading" className="text-[11px] text-[hsl(var(--ops-brand))] hover:underline flex items-center gap-1">View all <ArrowUpRight className="size-3" /></Link>
          </div>
          <PositionCards positions={data.positions} testId="ops-positions-preview-cards" />
          <div className="hidden md:block overflow-x-auto">
            <table data-testid="ops-positions-preview" className="w-full text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text-muted))]">
                  <th className="text-left py-2 px-3 font-medium">Instrument</th>
                  <th className="text-center py-2 px-3 font-medium">Dir</th>
                  <th className="text-right py-2 px-3 font-medium">Qty</th>
                  <th className="text-right py-2 px-3 font-medium">Entry</th>
                  <th className="text-right py-2 px-3 font-medium">Current</th>
                  <th className="text-right py-2 px-3 font-medium">Unrl P&L</th>
                  <th className="text-right py-2 px-3 font-medium">Stop</th>
                  <th className="text-right py-2 px-3 font-medium">Target</th>
                </tr>
              </thead>
              <tbody>
                {data.positions.map((p, i) => (
                  <tr key={i} className="border-b border-[hsl(var(--ops-border))]/50 hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-100">
                    <td className="py-2 px-3 font-medium text-[hsl(var(--ops-text))]">{p.instrument}</td>
                    <td className="py-2 px-3 text-center">
                      <Badge className={`rounded text-[10px] border-0 px-1.5 ${p.direction === 'LONG' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>
                        {p.direction}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-right font-mono-num text-[hsl(var(--ops-text))]">{p.quantity}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-[hsl(var(--ops-text))]">{p.entry_price?.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-[hsl(var(--ops-text))]">{p.current_price?.toFixed(2)}</td>
                    <td className={`py-2 px-3 text-right font-mono-num ${p.unrealized_pnl >= 0 ? 'ops-long' : 'ops-short'}`}>{fmtPnl(p.unrealized_pnl)}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-[hsl(var(--ops-warn))]">{p.stop_loss?.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono-num text-[hsl(var(--ops-brand))]">{p.profit_target?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
